<template>
  <div>
    <template v-if="!isOfficeLogin">
      <GradesMobile v-model="onboardingMobileActive" />
      <GradesDesktop v-model="onboardingDesktopActive"
        :isEmpty="Object.keys(groupedGrades).length === 0 && groupedGrades.constructor === Object" />
    </template>
    <div class="grades">
      <img class="grades__logo-mobile" src="@/assets/images/brand/logo.svg" />
      <img class="grades__logo-desktop" src="@/assets/images/brand/logo_full.svg" />
      <div class="grades__container">
        <div class="grades__header">
          <div class="grades__header-left">
            <h3 class="grades__title">
              {{ $t("dashboard.grades") }}
            </h3>
            <button v-if="(isFullAccess) && !isLoading && grades.length" @click="startOnlyOnboarding"
              class="mt-1 ml-3 onboarding-button">
              <div class="flex items-center">
                <img id="classes-homework" src="@/assets/images/icons/i_pink.svg" />
                <p class="ml-2 text-pink-main text-lg font-medium">Zobacz jak to działa</p>
              </div>
            </button>
          </div>
          <span class="system_monitor link" v-if="hasGroup && student.group.hasGrades && groupHasSymbols">
            <router-link class="monitor-system-progress" :to="{ name: 'Regulations.MonitorSystemProgress' }">
              <span class="flex content-center flex-wrap text-base md:text-lg">
                <div>
                  <span class="monitor-system-progress">{{ $t("dashboard.monitor_system_progress") }}</span>
                </div>
              </span>
            </router-link>
          </span>
        </div>
        <Loader v-if="isLoading" />
        <GradesEmptyState v-if="!isLoading && ((!isFullAccess) || !grades.length)" />
        <GradesList v-else-if="Object.keys(groupedGrades).length !== 0 && !isLoading" :grades="groupedGrades" />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import {
  mapActions, mapState, mapMutations, mapGetters,
} from 'vuex';

import GradesList from '@/components/GradesList/GradesList.vue';
import Loader from '@/components/Loader.vue';

import GradesMobile from '@/components/Popup/GradesMobile.vue';
import GradesDesktop from '@/components/Popup/GradesDesktop.vue';
import GradesEmptyState from '../../components/GradesList/GradesEmptyState.vue';

const NAMED_SYMBOL_GROUPS = [
  'Pre-First',
  'First',
  'Pre-Advanced',
  'Advanced',
  'EGZ',
  'EGZ-ONE',
];

export default {
  name: 'Grades',
  components: {
    GradesList,
    GradesMobile,
    GradesDesktop,
    Loader,
    GradesEmptyState,
  },

  data() {
    return {
      windowWidth: 0,
      isLoading: false,
      groupedGrades: {},
      onboardingMobileActive: false,
      onboardingDesktopActive: false,
    };
  },

  computed: {
    ...mapState('students', ['student']),
    ...mapState('grades', ['grades']),
    ...mapGetters('auth', ['isOfficeLogin', 'isFullAccess']),
    ...mapGetters('onboarding', [
      'getOnboardingProperty',
    ]),
    isMobileOnboardingDone() {
      return this.getOnboardingProperty({ deviceType: 'mobile', property: 'grades' });
    },
    isDesktopOnboardingDone() {
      return this.getOnboardingProperty({ deviceType: 'desktop', property: 'grades' });
    },

    hasGroup() {
      return !!this.student?.group;
    },

    // TRUE for groups 1-9 (and combinations, ex. 8.5/8.6, 1.1)
    // and for the named levels listed in NAMED_SYMBOL_GROUPS
    groupHasSymbols() {
      const groupNameV1 = this.student?.group?.parentFullName;
      const groupNameV2 = this.student?.group?.fullName;

      const hasSymbols = (groupName) => {
        if (!groupName) return false;

        const trimmedName = groupName.trim();

        const groupNumber = parseInt(trimmedName, 10);
        if (groupNumber >= 1 && groupNumber <= 9) return true;

        // named levels, ex. "Pre-Advanced B"
        return NAMED_SYMBOL_GROUPS.some(name => trimmedName.startsWith(name));
      };

      // check first group name, if there is no matching check the other one for sure
      return hasSymbols(groupNameV1) || hasSymbols(groupNameV2);
    },
  },

  methods: {
    ...mapActions('grades', ['getGrades']),
    ...mapActions('students', ['updateStudent']),
    ...mapActions('lessons', ['clearLesson', 'clearLessons']),
    ...mapActions('grades', ['clearGrades']),
    ...mapMutations('auth', ['setLogoutBlocked']),
    ...mapMutations('layout', ['showClassList', 'hideClassList']),
    ...mapMutations('layout', ['closeSidebar']),

    close() {
      this.closeSidebar();
    },

    getWindowWidth() {
      this.windowWidth = window.innerWidth;
      Event.$emit('cancelTour');
      // this.onboardingMobileActive = false;
      // this.onboardingDesktopActive = false;
    },

    async getData(params = {}) {
      // const dateFrom = moment('2025-08-26', 'YYYY-MM-DD');
      // const dateTo = moment('2026-06-30', 'YYYY-MM-DD');
      const dateFrom = moment('0901', 'MMDD');
      const dateTo = moment('0831', 'MMDD');
      if (dateFrom.month() > moment().month()) {
        dateFrom.subtract(1, 'year');
      } else {
        dateTo.add(1, 'year');
      }

      if (this.student?.id) {
        await this.getGrades({
          studentId: this.student.id,
          payload: Object.assign(
            {
              dateFrom: dateFrom.format('YYYY-MM-DD'),
              dateTo: dateTo.format('YYYY-MM-DD'),
            },
            params,
          ),
        });
      }

      this.groupGrades();
    },

    groupGrades() {
      const SEMESTER_GRADE_ID = 20;
      const ANNUAL_GRADE_ID = 21;
      const SEMESTER_MONTH_INDEX = 1;
      const ANNUAL_MONTH_INDEX = 5;

      const commonGroupedGrades = {};
      const examsGroupedGrades = {};
      let groupedGrades = {};
      const initiallySortedGrades = [];
      const sortedGrades = [];
      const connectedExamsGrades = [];

      // group grades by months
      this.grades.forEach((grade) => {
        const gradeDate = moment(grade.gradeDescription.lesson.date, 'YYYY-M');
        let gradeMonthName = gradeDate.format('YYYY-MM');
        const gradeCategoryId = grade.gradeDescription.gradeCategory.id;
        const isCurrentGradeFromAnnualCategory = gradeCategoryId === ANNUAL_GRADE_ID;
        const isCurrentGradeFromExamsCategory = gradeCategoryId === SEMESTER_GRADE_ID || gradeCategoryId === ANNUAL_GRADE_ID;

        // adjust assigned month to show annual exams after June
        if (isCurrentGradeFromExamsCategory && isCurrentGradeFromAnnualCategory) {
          gradeMonthName = gradeDate.set('month', ANNUAL_MONTH_INDEX).format('YYYY-MM');
        }

        groupedGrades = isCurrentGradeFromExamsCategory ? examsGroupedGrades : commonGroupedGrades;
        if (Object.prototype.hasOwnProperty.call(groupedGrades, gradeMonthName)) {
          groupedGrades[gradeMonthName].push(grade);
        } else {
          groupedGrades[gradeMonthName] = [grade];
        }
      });

      // sort grouped grades by months order
      Object.keys(commonGroupedGrades).forEach((key) => {
        initiallySortedGrades.push([key, commonGroupedGrades[key]]);
      });
      initiallySortedGrades.sort();

      // insert exams grades between months
      initiallySortedGrades.forEach((item, itemIndex) => {
        const [monthName] = item;
        const isCurrentMonthExamMonth = Object.prototype.hasOwnProperty.call(examsGroupedGrades, monthName);

        sortedGrades.push(item);
        if (isCurrentMonthExamMonth) {
          const examsMonthNumber = parseInt(moment(monthName, 'YYYY-MM').format('M'), 10);
          const examLabel = examsMonthNumber === SEMESTER_MONTH_INDEX ? 'semester' : 'annual';
          sortedGrades.push([`${examLabel}${itemIndex + 1}`, examsGroupedGrades[monthName]]);
          connectedExamsGrades.push(monthName);
        }
      });

      // add not assigned exams grades at the end of the data - mostly test feature
      (Object.entries(examsGroupedGrades)).forEach(([monthName, grades], index) => {
        if (!connectedExamsGrades.includes(monthName)) {
          const gradeCategoryId = grades[0].gradeDescription.gradeCategory.id;
          const examLabel = gradeCategoryId === SEMESTER_GRADE_ID ? 'semester' : 'annual';
          sortedGrades.push([`${examLabel}${(index + 1) * 1000}`, grades]);
        }
      });

      sortedGrades.reverse();

      // format output data for grades
      this.groupedGrades = {};
      sortedGrades.forEach(([groupName, groupGrades]) => {
        // eslint-disable-next-line prefer-destructuring
        this.groupedGrades[groupName] = groupGrades;
      });
    },

    startDesktopOnboarding() {
      this.onboardingDesktopActive = !this.onboardingDesktopActive;
    },

    startMobileOnboarding() {
      this.onboardingMobileActive = !this.onboardingMobileActive;
    },

    startOnlyOnboarding() {
      if (this.windowWidth < 992) {
        this.onboardingMobileActive = !this.onboardingMobileActive;
      } else {
        this.onboardingDesktopActive = !this.onboardingDesktopActive;
      }
    },

    checkIfTourCookieExist() {
      if (!this.student?.id || !this.student?.onboardingMobile || !this.student?.onboardingDesktop) return;

      const profileMobileOnboarding = this.student?.onboardingMobile?.grades;
      const profileDesktopOnboarding = this.student?.onboardingDesktop?.grades;

      setTimeout(() => {
        const isMobile = this.windowWidth < 992;

        if (isMobile) {
          if (!profileMobileOnboarding) {
            this.startMobileOnboarding();
            this.showClassList();
          }
        } else if (!profileDesktopOnboarding) {
          this.startDesktopOnboarding();
          this.showClassList();
        }
      }, 500);
    },
  },

  mounted() {
    this.windowWidth = window.innerWidth;
    window.addEventListener('resize', this.getWindowWidth);
  },

  async created() {
    if (!this.student || Object.keys(this.student).length === 0) {
      this.routerPush({ name: 'Auth.Welcome' });
    } else if (!this.isFullAccess) {
      this.isLoading = false;
      this.showClassList();
    } else {
      // this.setLogoutBlocked(true);
      this.hideClassList();
      this.isLoading = true;
      this.clearLesson();
      this.clearLessons();
      this.clearGrades();
      if (this.student?.id) {
        await this.updateStudent(this.student.id);
      }
      if (
        this.student === null
        || (this.$route.name === 'Main.Grades' && !this.student.group.hasGrades)
      ) {
        this.routerPush({ name: 'Auth.Welcome' });
        return;
      }
      await this.getData();
      this.isLoading = false;
      await this.checkIfTourCookieExist();
      // setTimeout(() => {
      //   this.setLogoutBlocked(false);
      // }, 500);
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.getWindowWidth);
  },
};
</script>

<style lang="scss">
.grades {
  @apply min-h-full pb-32 relative;
  overflow: hidden;
  padding-top: 30px;
  z-index: 1;

  @media screen and (min-width: 992px) {
    margin-left: 312px;
  }

  .link {
    a {
      text-decoration: none;
    }
  }

  &__container {
    @apply mx-auto w-full pt-8 px-4;
    padding-bottom: 120px;

    @media screen and (min-width: 992px) {
      padding-left: 78px;
      padding-right: 40px;
      padding-bottom: 30px;
    }
  }

  &__header {
    @apply flex flex-row justify-between items-start;

    @media screen and (min-width: 992px) {
      @apply items-center;
    }

    &-left {
      @apply flex flex-col items-start;

      @media screen and (min-width: 992px) {
        @apply flex-row items-center;
      }
    }

    .onboarding-button {
      @apply ml-0 mt-4;
      white-space: nowrap;

      @media screen and (min-width: 992px) {
        @apply mt-1 ml-4;
      }
    }

    .system_monitor {
      @apply text-xl pl-0 mt-1 font-light inline-block;

      @media screen and (min-width: 992px) {
        @apply mt-0 pl-8;
      }

      a {
        transition: color 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
        text-decoration: underline;
        text-decoration-skip-ink: none;

        &:hover {
          @apply text-grey-dark;
        }
      }
    }
  }

  &__logo-mobile {
    @apply w-14 h-14 mx-auto block;

    @media screen and (min-width: 992px) {
      @apply hidden;
    }
  }

  &__logo-desktop {
    @apply mt-4 block;

    @media screen and (max-width: 991px) {
      @apply hidden;
    }

    @media screen and (min-width: 992px) {
      transform: translateX(78px);
    }
  }

  &__title {
    @apply text-2xl font-semibold mb-0;

    @media screen and (min-width: 992px) {
      @apply text-4xl;
    }
  }

  .empty_state {
    @apply mt-8;
  }
}
</style>
